import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Text from '../../components/Text';
import Button from '../../components/Button';
import { ethers } from 'ethers';
import { notifyError } from '../../hooks/utils';

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
`;

const PopupContent = styled.div`
    background: #fff;
    padding: 2rem;
    border-radius: 8px;
    max-width: 400px;
    width: 90%;
    position: relative;
`;

const Title = styled.h2`
    color: #507c83;
    font-size: 1.5rem;
    margin-bottom: 2rem;
    text-align: center;
    font-family: "Press Start 2P", monospace;
`;

const InputContainer = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
    color: #507c83;
    font-family: "Press Start 2P", monospace;
    font-size: 0.8rem;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.5rem;
    border: 2px solid #507c83;
    border-radius: 4px;
    font-family: "Press Start 2P", monospace;
    font-size: 1rem;
    &:focus {
        outline: none;
        border-color: #3a5d63;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-top: 2rem;
`;

const StyledButton = styled(Button)`
    padding: 0.5rem 1rem;
    font-family: "Press Start 2P", monospace;
    font-size: 0.8em;
`;

interface CoordinatePopupProps {
    onClose: () => void;
    onConfirm: (x: number, y: number, address: string) => void;
    minerName: string;
}

const CoordinatePopup: React.FC<CoordinatePopupProps> = ({ onClose, onConfirm, minerName }) => {
    const [x, setX] = useState('');
    const [y, setY] = useState('');
    const [address, setAddress] = useState('');

    // Get connected wallet address on mount
    useEffect(() => {
        const getAddress = async () => {
            if (!(window as any).ethereum) return;
            const provider = new ethers.providers.Web3Provider((window as any).ethereum);
            const signer = provider.getSigner();
            const addr = await signer.getAddress();
            setAddress(addr);
        };
        getAddress();
    }, []);

    const handleConfirm = () => {
        const xNum = parseInt(x);
        const yNum = parseInt(y);
        if (isNaN(xNum) || isNaN(yNum)) {
            notifyError('Please enter valid numbers for coordinates');
            return;
        }
        if (!ethers.utils.isAddress(address)) {
            notifyError('Please enter a valid address');
            return;
        }
        onConfirm(xNum, yNum, address);
    };

    return (
        <PopupOverlay onClick={onClose}>
            <PopupContent onClick={e => e.stopPropagation()}>
                <Title>Place {minerName}</Title>
                <Text style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    Choose where to place your miner on the map
                </Text>
                
                <InputContainer>
                    <Label>Address:</Label>
                    <Input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Enter Ethereum address"
                    />
                </InputContainer>

                <InputContainer>
                    <Label>X Coordinate:</Label>
                    <Input
                        type="number"
                        value={x}
                        onChange={(e) => setX(e.target.value)}
                        placeholder="Enter X coordinate"
                    />
                </InputContainer>

                <InputContainer>
                    <Label>Y Coordinate:</Label>
                    <Input
                        type="number"
                        value={y}
                        onChange={(e) => setY(e.target.value)}
                        placeholder="Enter Y coordinate"
                    />
                </InputContainer>

                <ButtonContainer>
                    <StyledButton
                        variant="pixelAction"
                        size="md"
                        shape="square"
                        onClick={onClose}
                        style={{ background: '#ccc' }}
                    >
                        CANCEL
                    </StyledButton>
                    <StyledButton
                        variant="pixelAction"
                        size="md"
                        shape="square"
                        onClick={handleConfirm}
                        style={{ background: '#507c83', color: 'white' }}
                    >
                        CONFIRM
                    </StyledButton>
                </ButtonContainer>
            </PopupContent>
        </PopupOverlay>
    );
};

export default CoordinatePopup; 